import { useTradingSettings } from '@src/providers/AppConfigProvider';
import {
  Box,
  Button,
  ButtonVariants,
  Dialog,
  DialogFooterWrapper,
  EMPTY_ARRAY,
  FormControlSizes,
  HStack,
  Icon,
  IconName,
  IndicatorBadge,
  LoaderSizes,
  LoaderTalos,
  NotificationVariants,
  OrdStatusEnum,
  Stack,
  Text,
  Toggle,
  Tooltip,
  useCallbackRef,
  useGlobalToasts,
  usePortal,
  VStack,
  type CareOrder,
  type UseDisclosureWithContextReturn,
} from '@talos/kyoko';
import { useMemo, useState } from 'react';
import styled from 'styled-components';
import { useFeatureFlag } from '../../../../hooks';
import { useAppStateDispatch } from '../../../../providers/AppStateProvider';
import { getCareOrderCountLabel } from '../utils';
import { useActivateWorkingOrderMutation } from '../workingOrderActivationApi';
import { CareOrderNettingHeader } from './CareOrderNettingHeader';
import { CompatibleCareOrdersBlotter } from './CompatibleCareOrdersBlotter';
import { getCompatibleCareOrders } from './getCompatibleCareOrders';
import { MemberCareOrdersBlotter } from './MemberCareOrdersBlotter';
import { NettingSizeModeToggle } from './NettingSizeModeToggle';
import { getNewNettedOrderSubAccount, getSubAccountWarning } from './nettingSubAccount';
import { PreviewBlotter } from './PreviewBlotter';
import { submitCareOrderNettingThunk } from './submitCareOrderNetting';
import { useSelectableCareOrders } from './useSelectableCareOrders';
import {
  CareOrderNettingPreviewMode,
  isExistingCareOrderNettingPreviewMode,
  isReadOnlyCareOrderNettingPreviewMode,
  useWorkingOrderPreview,
  type CareOrderNettingPreviewMode as CareOrderNettingPreviewModeType,
} from './useWorkingOrderPreview';

export { CareOrderNettingPreviewMode };

export interface CareOrderNettingPreviewDialogContext {
  /** Create, modify, approve, or view a netted care order */
  mode: CareOrderNettingPreviewModeType;
  /** The starting selected care orders to prime the dialog with */
  selectedCareOrders: CareOrder[];
  /** Pass if modifying */
  nettedCareOrder?: CareOrder;
}

interface CareOrderNettingPreviewDialogProps {
  disclosure: UseDisclosureWithContextReturn<CareOrderNettingPreviewDialogContext>;
}

export function CareOrderNettingPreviewDialog({ disclosure }: CareOrderNettingPreviewDialogProps) {
  const context = disclosure.context;
  if (disclosure.openId == null || context == null) {
    return null;
  }

  return <CareOrderNettingPreviewDialogSession key={disclosure.openId} disclosure={disclosure} context={context} />;
}

interface CareOrderNettingPreviewDialogSessionProps {
  disclosure: UseDisclosureWithContextReturn<CareOrderNettingPreviewDialogContext>;
  context: CareOrderNettingPreviewDialogContext;
}

const getPreviewSectionTitle = (mode: CareOrderNettingPreviewModeType) =>
  mode === CareOrderNettingPreviewMode.Modify ? 'Netting Modification Preview' : 'Preview Netted Order';

function CareOrderNettingPreviewDialogSession({ disclosure, context }: CareOrderNettingPreviewDialogSessionProps) {
  const { mode, selectedCareOrders: initialMemberCareOrders } = context;
  const dispatch = useAppStateDispatch();
  const [activateWorkingOrder] = useActivateWorkingOrderMutation();
  const { workingRequireOrderActivation } = useFeatureFlag();
  const { careOrderNettingPreviewShowAllDecimals, setCareOrderNettingPreviewShowAllDecimals } = useTradingSettings();
  const { add: addToast } = useGlobalToasts();
  const memberCareOrdersFiltersPortalId = 'care-order-netting-member-care-orders-filters';
  const compatibleCareOrdersFiltersPortalId = 'care-order-netting-compatible-care-orders-filters';
  const { setPortalRef: setMemberCareOrdersFiltersPortalRef } = usePortal(memberCareOrdersFiltersPortalId);
  const { setPortalRef: setCompatibleCareOrdersFiltersPortalRef } = usePortal(compatibleCareOrdersFiltersPortalId);
  const isModifyMode = mode === CareOrderNettingPreviewMode.Modify;
  const isExistingNettedOrder = isExistingCareOrderNettingPreviewMode(mode);
  const isApprovalMode = mode === CareOrderNettingPreviewMode.Approval;
  const isViewMode = mode === CareOrderNettingPreviewMode.View;
  const isReadOnlyMode = isReadOnlyCareOrderNettingPreviewMode(mode);

  // Read-only modes inspect the persisted netted order; create/modify modes build a mutable preview from selected members.
  const nettedCareOrder = isModifyMode || isReadOnlyMode ? context.nettedCareOrder : undefined;
  const minimumSelectedCareOrders = 1;

  // Each dialog session has a fixed reference care order that defines the "type" of care order we can net.
  // Modify Netting can be opened while an arbitrary grid row is selected, so reference the working order being
  // modified instead of the parent-grid selection to keep valid add-back/addition rows visible.
  const [referenceCareOrder] = useState(() => (isModifyMode ? nettedCareOrder : initialMemberCareOrders.at(0)));

  // We track loading action as either confirm or confirmAndRFQ just so we only show the spinner in one of them. Both will be disabled though.
  const [loadingAction, setLoadingAction] = useState<'confirm' | 'confirmAndRFQ' | undefined>();
  const [showCompatibleCareOrders, setShowCompatibleCareOrders] = useState(false);
  const {
    allCareOrders,
    memberCareOrders,
    memberCareOrderIDs,
    allMemberCareOrdersLoaded,
    sizeMode,
    canSelectUnitsSizeMode,
    isSizeModeDisabled,
    onSizeModeChange,
    onSelectionChanged,
  } = useSelectableCareOrders({
    mode,
    initialMemberCareOrders,
    nettedCareOrder,
  });

  const previewCareOrderIDs = allMemberCareOrdersLoaded ? memberCareOrderIDs : EMPTY_ARRAY;

  const resolvedSubAccount = useMemo(
    () => (isModifyMode ? nettedCareOrder?.SubAccount : getNewNettedOrderSubAccount(memberCareOrders)),
    [isModifyMode, memberCareOrders, nettedCareOrder]
  );
  const subAccountWarning = useMemo(
    () =>
      // we only care about the SA warning if we are writing
      isReadOnlyMode
        ? undefined
        : getSubAccountWarning({
            isExistingNettedOrder,
            memberCareOrders,
            nettedCareOrder,
          }),
    [isExistingNettedOrder, isReadOnlyMode, memberCareOrders, nettedCareOrder]
  );

  const {
    workingOrder: previewWorkingOrder,
    loading: previewLoading,
    previewError,
  } = useWorkingOrderPreview({
    mode,
    careOrderIDs: previewCareOrderIDs,
    orderID: nettedCareOrder?.OrderID,
    existingWorkingOrder: isReadOnlyMode ? nettedCareOrder : undefined,
    sizeMode,
    subAccount: resolvedSubAccount,
    addToast,
  });

  const previewActionLabel = getPreviewActionLabel(mode);
  const previewInvalidTooltip = getPreviewInvalidTooltip({ mode, previewWorkingOrder });
  const confirmDisabled = getConfirmDisabled({
    isApprovalMode,
    loadingAction,
    memberCareOrderCount: memberCareOrderIDs.length,
    minimumSelectedCareOrders,
    nettedCareOrder,
    previewWorkingOrder,
  });

  const handleConfirm = useCallbackRef(async ({ primeRFQ }: { primeRFQ: boolean }) => {
    setLoadingAction(primeRFQ ? 'confirmAndRFQ' : 'confirm');
    try {
      if (isModifyMode) {
        if (nettedCareOrder == null) {
          return;
        }
      }

      // Both footer actions share the same netting request; the RFQ variant primes from the returned WorkingOrder.
      await dispatch(
        submitCareOrderNettingThunk({
          memberCareOrderIDs,
          orderID: nettedCareOrder?.OrderID,
          primeRFQ,
          sizeMode,
          subAccount: isModifyMode ? undefined : resolvedSubAccount,
        })
      ).unwrap();

      addToast({
        text: isModifyMode
          ? `Successfully modified netted order with ${memberCareOrderIDs.length} care order(s).`
          : `Successfully netted ${memberCareOrderIDs.length} care orders.`,
        variant: NotificationVariants.Positive,
      });
      disclosure.close();
    } catch (error) {
      addToast({
        text: `Could not ${previewActionLabel}: ${error instanceof Error ? error.message : 'Unknown error'}`,
        variant: NotificationVariants.Negative,
      });
    } finally {
      setLoadingAction(undefined);
    }
  });

  const handleApprove = useCallbackRef(async () => {
    if (nettedCareOrder == null) {
      return;
    }

    setLoadingAction('confirm');
    try {
      await activateWorkingOrder({
        orderID: nettedCareOrder.OrderID,
        targetRevision: nettedCareOrder.Revision,
      }).unwrap();
      disclosure.close();
    } finally {
      setLoadingAction(undefined);
    }
  });

  const memberCareOrdersCount = memberCareOrderIDs.length;
  const compatibleCareOrdersCount = useMemo(
    () =>
      getCompatibleCareOrders({ allCareOrders, isModificationMode: isModifyMode, referenceCareOrder, sizeMode }).length,
    [allCareOrders, isModifyMode, referenceCareOrder, sizeMode]
  );
  const showAllDecimals = careOrderNettingPreviewShowAllDecimals;
  const primaryActionLabel = getPrimaryActionLabel(mode, memberCareOrderIDs.length);

  return (
    <Dialog
      isOpen={disclosure.isOpen}
      close={disclosure.close}
      onCloseComplete={() => {
        disclosure.onCloseComplete();
      }}
      title="Care Order Netting Preview"
      headerSuffix={
        <Toggle
          label="Show All Decimals"
          size={FormControlSizes.Small}
          checked={showAllDecimals}
          onChange={setCareOrderNettingPreviewShowAllDecimals}
          data-testid="care-order-netting-preview-show-decimals-toggle"
        />
      }
      confirmLabel={primaryActionLabel}
      confirmDisabled={confirmDisabled}
      confirmButtonTooltip={isReadOnlyMode ? undefined : previewInvalidTooltip}
      closeOnConfirm={false}
      customActions={
        <DialogFooterWrapper>
          <Button data-testid="dialog-cancel" type="button" disabled={loadingAction != null} onClick={disclosure.close}>
            Close
          </Button>

          {!isViewMode && (
            <>
              {/* Keep the original dialog-confirm test id for the existing netting page object/tests. */}
              <Tooltip tooltip={isApprovalMode ? undefined : previewInvalidTooltip}>
                <NettingActionButton
                  data-testid="dialog-confirm"
                  type="button"
                  variant={ButtonVariants.Primary}
                  disabled={confirmDisabled}
                  loading={loadingAction === 'confirm'}
                  onClick={() => {
                    if (isApprovalMode) {
                      void handleApprove();
                    } else {
                      void handleConfirm({ primeRFQ: false });
                    }
                  }}
                >
                  {primaryActionLabel}
                </NettingActionButton>
              </Tooltip>
              {/* Approval-required working orders cannot be RFQed until a second trader activates them. */}
              {!isApprovalMode && !workingRequireOrderActivation && (
                <Tooltip tooltip={previewInvalidTooltip}>
                  <NettingActionButton
                    data-testid="care-order-netting-confirm-and-rfq-button"
                    type="button"
                    variant={ButtonVariants.Primary}
                    disabled={confirmDisabled}
                    loading={loadingAction === 'confirmAndRFQ'}
                    onClick={() => handleConfirm({ primeRFQ: true })}
                  >
                    {isModifyMode
                      ? 'Modify Netted Order & Prime RFQ'
                      : `Net ${getCareOrderCountLabel(memberCareOrderIDs.length)} & Prime RFQ`}
                  </NettingActionButton>
                </Tooltip>
              )}
            </>
          )}
        </DialogFooterWrapper>
      }
      minWidth="960px"
      width="90vw"
      maxWidth="1600px"
      minHeight="750px"
      height="90vh"
      style={{ maxHeight: '900px' }}
      contentPadding="0px"
      dataTestId="care-order-netting-preview-dialog"
      preventClosing={loadingAction != null}
      showClose
    >
      <VStack
        w="100%"
        flex={1}
        alignItems="stretch"
        data-testid="care-order-netting-preview"
        gap="spacing1"
        background="backgroundModal"
      >
        <CareOrderNettingHeader
          workingOrder={previewWorkingOrder}
          memberCareOrders={memberCareOrders}
          showAllDecimals={showAllDecimals}
        />
        <Box h="100%">
          <Stack h="100%">
            <BlotterHeader>
              <HStack justifyContent="space-between" w="100%">
                <HStack gap="spacing6">
                  <Text color="colorTextImportant" fontSize="fontSizeSm">
                    {getPreviewSectionTitle(mode)}
                  </Text>
                  {previewError != null ? (
                    <Tooltip tooltip={previewError}>
                      <Box data-testid="care-order-netting-preview-error-icon">
                        <Icon icon={IconName.ExclamationCircle} color="colors.red.lighten" />
                      </Box>
                    </Tooltip>
                  ) : (
                    previewLoading && (
                      <Box>
                        <LoaderTalos size={LoaderSizes.XXS} />
                      </Box>
                    )
                  )}
                </HStack>

                {!isReadOnlyMode && (
                  <NettingSizeModeToggle
                    value={sizeMode}
                    canSelectUnits={canSelectUnitsSizeMode}
                    disabled={isSizeModeDisabled}
                    onChange={onSizeModeChange}
                  />
                )}
              </HStack>
            </BlotterHeader>
            <Box h="80px" data-testid="care-order-netting-preview-working-order">
              <PreviewBlotter previewWorkingOrder={previewWorkingOrder} showAllDecimals={showAllDecimals} />
            </Box>

            <Stack flex={1}>
              <BlotterHeader>
                <HStack gap="spacing6">
                  <Text fontSize="fontSizeSm" color="colorTextImportant">
                    Member Care Orders
                  </Text>
                  <IndicatorBadge>{memberCareOrdersCount}</IndicatorBadge>
                </HStack>
                <Box ref={setMemberCareOrdersFiltersPortalRef} />
              </BlotterHeader>
              <MemberCareOrdersBlotter
                portalId={memberCareOrdersFiltersPortalId}
                showAllDecimals={showAllDecimals}
                memberCareOrders={memberCareOrders}
                onSelectionChanged={onSelectionChanged}
                onShowCompatibleCareOrders={() => setShowCompatibleCareOrders(true)}
                showCompatibleCareOrders={showCompatibleCareOrders}
                readOnly={isReadOnlyMode}
                subAccountWarning={subAccountWarning}
              />
            </Stack>
            {!isReadOnlyMode && showCompatibleCareOrders && (
              <Stack flex={1}>
                <BlotterHeader>
                  <HStack gap="spacing6">
                    <Text fontSize="fontSizeSm" color="colorTextImportant">
                      Compatible Care Orders
                    </Text>
                    <IndicatorBadge>{compatibleCareOrdersCount}</IndicatorBadge>
                  </HStack>
                  <Box ref={setCompatibleCareOrdersFiltersPortalRef} />
                </BlotterHeader>
                <CompatibleCareOrdersBlotter
                  portalId={compatibleCareOrdersFiltersPortalId}
                  isModificationMode={isModifyMode}
                  showAllDecimals={showAllDecimals}
                  allCareOrders={allCareOrders}
                  referenceCareOrder={referenceCareOrder}
                  sizeMode={sizeMode}
                  onSelectionChanged={onSelectionChanged}
                  onClose={() => setShowCompatibleCareOrders(false)}
                />
              </Stack>
            )}
          </Stack>
        </Box>
      </VStack>
    </Dialog>
  );
}

// Verb for preview-related error messages.
const getPreviewActionLabel = (mode: CareOrderNettingPreviewModeType) => {
  if (mode === CareOrderNettingPreviewMode.Approval) {
    return 'approve netted order';
  }

  if (mode === CareOrderNettingPreviewMode.Modify) {
    return 'modify netted order';
  }

  return 'net care orders';
};

// Explains why the preview cannot currently be confirmed.
const getPreviewInvalidTooltip = ({
  mode,
  previewWorkingOrder,
}: {
  mode: CareOrderNettingPreviewModeType;
  previewWorkingOrder?: CareOrder;
}) => {
  const previewActionLabel = getPreviewActionLabel(mode);

  if (previewWorkingOrder == null) {
    return `Unable to ${previewActionLabel} because preview order is unavailable.`;
  }

  if (previewWorkingOrder.OrdStatus === OrdStatusEnum.Rejected) {
    return `Unable to ${previewActionLabel} because preview order status is ${previewWorkingOrder.OrdStatus}.`;
  }

  return undefined;
};

// Centralizes the confirm button disable rules across create, modify, and approval modes.
const getConfirmDisabled = ({
  isApprovalMode,
  loadingAction,
  memberCareOrderCount,
  minimumSelectedCareOrders,
  nettedCareOrder,
  previewWorkingOrder,
}: {
  isApprovalMode: boolean;
  loadingAction?: 'confirm' | 'confirmAndRFQ';
  memberCareOrderCount: number;
  minimumSelectedCareOrders: number;
  nettedCareOrder?: CareOrder;
  previewWorkingOrder?: CareOrder;
}) => {
  if (loadingAction != null) {
    return true;
  }

  // Approval only needs a persisted netted order to activate.
  if (isApprovalMode) {
    return nettedCareOrder == null;
  }

  // Create/modify flows need at least one member care order.
  if (memberCareOrderCount < minimumSelectedCareOrders) {
    return true;
  }

  // The preview working order is required for submit and RFQ priming.
  if (previewWorkingOrder == null) {
    return true;
  }

  // Rejected previews cannot be submitted.
  return previewWorkingOrder.OrdStatus === OrdStatusEnum.Rejected;
};

// Keeps the primary footer button text in sync with the dialog mode.
const getPrimaryActionLabel = (mode: CareOrderNettingPreviewModeType, memberCareOrderCount: number) => {
  if (mode === CareOrderNettingPreviewMode.View) {
    return 'Close';
  }

  if (mode === CareOrderNettingPreviewMode.Approval) {
    return 'Approve';
  }

  if (mode === CareOrderNettingPreviewMode.Modify) {
    return 'Modify Netted Order';
  }

  return `Net ${getCareOrderCountLabel(memberCareOrderCount)}`;
};

const BlotterHeader = styled(HStack)`
  height: 40px;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing6}px;
  padding: 0 ${({ theme }) => theme.spacing16}px;
  border-top: 1px solid ${({ theme }) => theme.backgroundBody};
  border-bottom: 1px solid ${({ theme }) => theme.backgroundBody};
`;

const NettingActionButton = styled(Button)`
  min-width: 120px;
`;
